import AppButton from '@nxsys/components/AppButton';
import AppLoader from '@nxsys/components/AppLoader';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { defaultPaymentFrequency, paymentModels } from '@nxsys/constants/AppConstants';
import { useSubmitLeaverFPS } from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetLeaverFPSListParams } from '@nxsys/store/slices/dashboard/types';
import { SubmitLeaverFPSParams } from '@nxsys/types/api/dashboard';
import { ICompany } from '@nxsys/types/models/company';
import { LeaverFPSRecordType } from '@nxsys/types/models/RTI';
import { showMessage } from '@nxsys/utils/message';
import { getBackendMessage } from '@nxsys/utils/getErrorMessage';
import { ColumnsType } from 'antd/lib/table';
import { debounce, find } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const LeaverFPS: React.FC = () => {
  const dispatch = useDispatch();
  const { leaverFPSList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = leaverFPSList;

  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const { isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      companyPaymentId: selectedCompany?.id || undefined,
    });
  }, [selectedCompany]);

  const handleFetchData = (params: GetLeaverFPSListParams) => {
    dispatch(dashboardActions.getLeaverFPSList(params));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const columns: ColumnsType<LeaverFPSRecordType> = [
    {
      title: 'Employee ID',
      dataIndex: 'subcontractor',
      width: 150,
      render: (value) => value?.id || '',
    },
    {
      title: 'Forename',
      dataIndex: 'subcontractor',
      render: (value) => value?.forename || '',
      width: 180,
    },
    {
      title: 'Surname',
      dataIndex: 'subcontractor',
      render: (value) => value?.surname || '',
      width: 180,
    },
    {
      key: 'payFrequency',
      title: 'Pay Frequency',
      dataIndex: 'payFrequency',
      render: (value) =>
        find(defaultPaymentFrequency, (item) => item?.value === value)?.label ||
        '',
      width: 150,
    },
    {
      key: 'paymentModel',
      title: 'Payment Model',
      dataIndex: 'paymentModel',
      render: (value) => paymentModels[value as keyof typeof paymentModels] || value || '',
      width: 150,
    },
    {
      title: 'Company Payment',
      dataIndex: 'companyPayment',
      render: (value) => value?.companyName || '',
      width: 200,
    },
    {
      title: 'Date Left',
      dataIndex: 'dateLeft',
      align: 'right',
      render: (date) =>
        date ? moment(date, 'Europe/London').format('DD/MM/YYYY') : '',
    },
  ];

  const { loading: submitLeaverFPSLoading, run: submitLeaverFPS } =
    useSubmitLeaverFPS();
  const handleSubmitLeaverFPS = async () => {
    // A bureau employer may only file for their own companies, and the backend
    // refuses them an empty companyId (which files for the whole tenant), so
    // "no company picked" means each of their companies in turn.
    const companyIds: (string | null)[] = selectedCompany?.id
      ? [selectedCompany.id]
      : isBureauEmployer && user?.companyUserIds?.length
        ? user.companyUserIds
        : [null];
    let failure: string | undefined;
    for (const companyId of companyIds) {
      const params: SubmitLeaverFPSParams = { companyId };
      const res = await submitLeaverFPS(params);
      if (!res?.success) {
        failure = getBackendMessage(res) || 'Leaver FPS submission failed';
        break;
      }
    }
    if (!failure) {
      showMessage({
        type: 'success',
        content: 'Leaver FPS submitted successfully',
      });
    } else {
      showMessage({
        type: 'error',
        content: failure,
      });
    }
    refetchData();
    window.dispatchEvent(new CustomEvent('dashboard:reload-rti'));
  };

  return (
    <div className="NormalFPS">
      {submitLeaverFPSLoading && <AppLoader />}
      <AppsContent>
        <div className="flex items-center justify-between gap-2 w-full my-[20px] flex-wrap px-6">
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            placeholder="Select Payment Company"
            onChangeValue={(value: any) => {
              setSelectedCompany(value?.fullData);
            }}
            paramFilter={{ enableRTI: true }}
          />
          <div className="flex items-center gap-2">
            <AppInputSearch onChange={handleSearch} />
            <AppButton name="Submit" onClick={handleSubmitLeaverFPS} />
          </div>
        </div>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1300 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </div>
  );
};

export default LeaverFPS;
